import {
  DocumentTextIcon,
  TrashIcon,
  InformationCircleIcon,
  SparklesIcon,
  ArrowDownTrayIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import React, { useState, useEffect, useCallback } from 'react';

import ConfirmableButton from '../../../components/ConfirmableButton';
import Filename from '../../../components/Filename';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { UploadedReceipt } from '../../../types';
import { useGenerateReceiptBooking } from '../hooks/useGenerateReceiptBooking';
import { useReceiptActions } from '../hooks/useReceiptActions';
import { useRetryReceiptExtraction } from '../hooks/useRetryReceiptExtraction';

import ReceiptStatusBadge from './ReceiptStatusBadge';

interface ReceiptItemProps {
  receipt: UploadedReceipt;
  onRemove?: (receiptId: string) => void;
  isRemoving?: boolean;
  isGeneratingAll?: boolean;
  isRetryingAll?: boolean;
}

const ActionSpinner: React.FC<{ color?: string }> = ({ color }) => (
  <div className='p-2'>
    <LoadingSpinner size='md' {...(color ? { color } : {})} />
  </div>
);

const IMPORTABLE_STATUSES = ['generated', 'modified', 'import-failed'];
const ACTION_BUTTON_CLASS =
  'p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200';
const CONFIRM_BUTTON_CLASS =
  'p-1 bg-white dark:bg-gray-700 rounded-full border border-green-600 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20';
const CANCEL_BUTTON_CLASS =
  'p-1 bg-white dark:bg-gray-700 rounded-full border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600';

const ReceiptItem: React.FC<ReceiptItemProps> = ({
  receipt,
  onRemove,
  isRemoving = false,
  isGeneratingAll = false,
  isRetryingAll = false,
}) => {
  const { generateBooking, isGenerating } = useGenerateReceiptBooking();
  const { retryExtraction, isRetrying } = useRetryReceiptExtraction();
  const { importReceipt, rejectReceipt, isImporting, isRejecting } =
    useReceiptActions();

  const [isGenerationScheduling, setIsGenerationScheduling] = useState(false);

  const handleGenerateBooking = useCallback(async () => {
    setIsGenerationScheduling(true);
    const success = await generateBooking(receipt.id);
    if (!success) {
      setIsGenerationScheduling(false);
    }
  }, [generateBooking, receipt.id]);

  useEffect(() => {
    if (isGenerationScheduling && receipt.status !== 'ready') {
      setIsGenerationScheduling(false);
    }
  }, [receipt.status, isGenerationScheduling]);

  const NON_REMOVABLE_STATUSES = [
    'import-scheduled',
    'importing',
    'imported',
    'import-failed',
  ];
  const canRemove =
    !NON_REMOVABLE_STATUSES.includes(receipt.status) && onRemove !== undefined;

  return (
    <div className='p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center space-x-3 flex-1 min-w-0'>
          <DocumentTextIcon className='w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0' />
          <div className='flex-1 min-w-0'>
            <Filename
              storageKey={receipt.storageKey}
              fileName={receipt.originalFileName}
              className='text-sm font-medium truncate'
            />
            <p className='text-xs text-gray-500 dark:text-gray-400 mt-0.5'>
              Receipt ID: {receipt.id}
            </p>
            {receipt.status === 'modified' && (
              <p className='text-xs text-gray-500 dark:text-gray-400 italic mt-0.5 flex items-center'>
                <InformationCircleIcon className='w-3 h-3 mr-1' />
                Booking data modified
              </p>
            )}
          </div>
        </div>
        <div className='flex items-center space-x-3 ml-4'>
          <ReceiptStatusBadge status={receipt.status} />
          {(receipt.status === 'pending' ||
            receipt.status === 'generating' ||
            receipt.status === 'generation-scheduled' ||
            (isGeneratingAll && receipt.status === 'ready') ||
            (isRetryingAll && receipt.status === 'text-extraction-failed')) && (
            <ActionSpinner />
          )}
          {receipt.status === 'ready' &&
            (isGenerating || isGenerationScheduling ? (
              <ActionSpinner />
            ) : (
              <ConfirmableButton
                onConfirm={handleGenerateBooking}
                buttonClassName={ACTION_BUTTON_CLASS}
                confirmButtonClassName={CONFIRM_BUTTON_CLASS}
                cancelButtonClassName={CANCEL_BUTTON_CLASS}
                containerClassName='flex gap-1'
                confirmationMessage='Generate booking?'
                tooltip='Generate booking data'
              >
                <SparklesIcon className='w-5 h-5' />
              </ConfirmableButton>
            ))}
          {receipt.status === 'text-extraction-failed' &&
            (isRetrying ? (
              <ActionSpinner />
            ) : (
              <ConfirmableButton
                onConfirm={() => retryExtraction(receipt.id)}
                buttonClassName={ACTION_BUTTON_CLASS}
                confirmButtonClassName={CONFIRM_BUTTON_CLASS}
                cancelButtonClassName={CANCEL_BUTTON_CLASS}
                containerClassName='flex gap-1'
                confirmationMessage='Retry extraction?'
                tooltip='Retry text extraction'
              >
                <DocumentTextIcon className='w-5 h-5' />
              </ConfirmableButton>
            ))}
          {IMPORTABLE_STATUSES.includes(receipt.status) && (
            <>
              {isImporting ? (
                <ActionSpinner />
              ) : (
                <ConfirmableButton
                  onConfirm={() => importReceipt(receipt.id, false)}
                  buttonClassName={ACTION_BUTTON_CLASS}
                  confirmButtonClassName={CONFIRM_BUTTON_CLASS}
                  cancelButtonClassName={CANCEL_BUTTON_CLASS}
                  containerClassName='flex gap-1'
                  confirmationMessage='Import this booking?'
                  tooltip='Import booking'
                >
                  <ArrowDownTrayIcon className='w-5 h-5' />
                </ConfirmableButton>
              )}
              {isRejecting ? (
                <ActionSpinner />
              ) : (
                <ConfirmableButton
                  onConfirm={() => rejectReceipt(receipt.id)}
                  buttonClassName={ACTION_BUTTON_CLASS}
                  confirmButtonClassName={CONFIRM_BUTTON_CLASS}
                  cancelButtonClassName={CANCEL_BUTTON_CLASS}
                  containerClassName='flex gap-1'
                  confirmationMessage='Reject this booking?'
                  tooltip='Reject booking'
                >
                  <XCircleIcon className='w-5 h-5' />
                </ConfirmableButton>
              )}
            </>
          )}
          {canRemove && (
            <div>
              {isRemoving ? (
                <ActionSpinner color='border-red-600 dark:border-red-400' />
              ) : (
                <ConfirmableButton
                  onConfirm={() => onRemove(receipt.id)}
                  buttonClassName='p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200'
                  confirmButtonClassName='p-1 bg-white dark:bg-gray-700 rounded-full border border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'
                  cancelButtonClassName='p-1 bg-white dark:bg-gray-700 rounded-full border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600'
                  containerClassName='flex gap-1'
                >
                  <TrashIcon className='w-5 h-5' />
                </ConfirmableButton>
              )}
            </div>
          )}
        </div>
      </div>
      {(receipt.status === 'text-extraction-failed' ||
        receipt.status === 'generation-failed') &&
        receipt.errorMessage && (
          <div className='mt-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded'>
            <p className='text-xs text-red-600 dark:text-red-400 whitespace-pre-wrap'>
              Error: {receipt.errorMessage}
            </p>
            {receipt.errorMessage.includes(
              'Lex Office API key not configured'
            ) && (
              <a
                href='https://acconto-public-assets-dev.s3.eu-central-1.amazonaws.com/So+erstellst+du+einen+API.pdf'
                target='_blank'
                rel='noopener noreferrer'
                className='text-xs text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300 mt-1 inline-block'
              >
                Learn how to create an API key
              </a>
            )}
          </div>
        )}
    </div>
  );
};

export default ReceiptItem;
